import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Avatar,
  Divider,
  Modal,
  IconButton,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VideocamIcon from "@mui/icons-material/Videocam";

// Helper function to format date/time in the event's timezone
const formatEventDateTime = (dateTimeString, timeZone) => {
  const date = new Date(dateTimeString);
  
  const options = {
    timeZone: timeZone,
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  };
  
  return date.toLocaleString('en-US', options);
};

const EventDetailsPopup = ({ event, onClose }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedDescription, setEditedDescription] = useState(
    event?.description || ""
  );

  if (!event) return null;

  // Get the event's timezone
  const timeZone = event.start?.timeZone || 'UTC';
  
  const isAllDay = !event.start.dateTime;
  
  // Calculate duration
  const start = new Date(event.start.dateTime || event.start.date);
  const end = new Date(event.end.dateTime || event.end.date);
  const duration = Math.round((end - start) / (1000 * 60));

  // Format start and end times in the event's timezone
  const startFormatted = isAllDay 
    ? start.toLocaleDateString('en-US', { timeZone })
    : formatEventDateTime(event.start.dateTime, timeZone);
    
  const endFormatted = isAllDay
    ? end.toLocaleDateString('en-US', { timeZone })
    : new Date(event.end.dateTime).toLocaleTimeString('en-US', {
        timeZone: timeZone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });

  const handleSave = () => {
    setIsEditing(false);
    // Add save logic here
  };

  return (
    <Modal open={true} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", md: 600 },
          maxHeight: "90vh",
          bgcolor: "background.paper",
          boxShadow: 24,
          borderRadius: 3,
          overflow: "auto",
          p: 4,
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
            {event.summary}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <AccessTimeIcon sx={{ mr: 1, color: "#0d3559" }} />
            <Box>
              <Typography variant="body1" color="text.secondary">
                {isAllDay
                  ? startFormatted
                  : `${startFormatted} - ${endFormatted} (${duration} min)`}
              </Typography>
              {!isAllDay && timeZone !== 'UTC' && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Timezone: {timeZone}
                </Typography>
              )}
            </Box>
          </Box>

          {event.hangoutLink && (
            <Button
              variant="contained"
              startIcon={<VideocamIcon />}
              href={event.hangoutLink}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                borderRadius: 6,
                background: "#0d3559",
                textTransform: "none",
                "&:hover": {
                  background: "rgb(237,108,2)",
                }
              }}
            >
              Join
            </Button>
          )}
        </Box>
        <Divider sx={{ mb: 1 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6">Description</Typography>
          <Typography sx={{ bgcolor: "background.default", borderRadius: 2, p: 2, mt: 1, whiteSpace: 'pre-wrap' }}>
            {editedDescription || "No description"}
          </Typography>
        </Box>

        {event.attendees && event.attendees.length > 0 && (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Attendees
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {event.attendees.map((attendee) => (
                  <Box key={attendee.displayName || attendee.email} sx={{ position: 'relative', mb: 2 }}>
                    <Chip
                      avatar={
                        <Avatar sx={{ bgcolor: "#ffffff" }}>
                          {attendee.displayName 
                            ? attendee.displayName.charAt(0).toUpperCase() 
                            : attendee.email.charAt(0).toUpperCase()}
                        </Avatar>
                      }
                      label={attendee.displayName || attendee.email}
                      variant="outlined"
                      sx={{
                        color:
                          attendee.responseStatus === "accepted"
                            ? "success.main"
                            : attendee.responseStatus === "declined"
                            ? "error.main"
                            : "text.secondary",
                      }}
                    />
                    <Typography
                      sx={{
                        position: 'absolute',
                        bottom: -16,
                        left: 0,
                        right: 0,
                        textAlign: 'center',
                        fontSize: '0.75rem',
                        color:
                          attendee.responseStatus === "accepted"
                            ? "success.main"
                            : attendee.responseStatus === "declined"
                            ? "error.main"
                            : "text.secondary",
                      }}
                    >
                      {attendee.responseStatus 
                        ? attendee.responseStatus.charAt(0).toUpperCase() + attendee.responseStatus.slice(1) 
                        : "No Response"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default EventDetailsPopup;